import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Handshake,
    ClipboardList,
    FileText,
    ScrollText,
    Building2,
    CreditCard,
    Globe
} from 'lucide-react';

const ParallaxBusinessSetup = () => {
    const [activeStep, setActiveStep] = useState(null);

    const steps = [
        { stepNumber: 1, Icon: Handshake, title: "Initial Consultation", content: "Personalized strategic session to understand your business objectives and Dubai market potential.", color: "text-blue-600", ringColor: "ring-blue-200" },
        { stepNumber: 2, Icon: ClipboardList, title: "Business Structure", content: "Expert guidance in choosing the most suitable business entity for your Dubai venture.", color: "text-blue-700", ringColor: "ring-blue-300" },
        { stepNumber: 3, Icon: FileText, title: "Documentation", content: "Comprehensive support in preparing and authenticating all required legal documents.", color: "text-blue-800", ringColor: "ring-blue-400" },
        { stepNumber: 4, Icon: ScrollText, title: "License Application", content: "Streamlined process for obtaining the appropriate business license from Dubai authorities.", color: "text-blue-900", ringColor: "ring-blue-500" },
        { stepNumber: 5, Icon: Building2, title: "Office & Visas", content: "Assistance in securing a corporate address and initiating necessary visa procedures.", color: "text-indigo-600", ringColor: "ring-blue-600" },
        { stepNumber: 6, Icon: CreditCard, title: "Bank Setup", content: "Simplified process for establishing corporate banking relationships in Dubai.", color: "text-indigo-700", ringColor: "ring-blue-700" },
        { stepNumber: 7, Icon: Globe, title: "Final Configuration", content: "Ensuring complete regulatory compliance and providing ongoing business support.", color: "text-indigo-800", ringColor: "ring-blue-800" }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-25 to-blue-50 py-16 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-5xl font-extrabold tracking-tight bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 inline-block text-transparent bg-clip-text">
                        Your Dubai Business Journey
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mt-4">
                        Transform your business aspirations into reality with our comprehensive Dubai business setup services.
                    </p>
                </motion.div>

                {/* Steps */}
                <div className="flex flex-col gap-6">
                    {steps.map((step) => (
                        <motion.div
                            key={step.stepNumber}
                            layout
                            transition={{ layout: { duration: 0.5, type: "spring" } }}
                            className={`
                                relative bg-white rounded-2xl shadow-lg 
                                overflow-hidden cursor-pointer transition-all 
                                ${activeStep === step.stepNumber ? "ring-4 scale-105 " + step.ringColor : "hover:scale-101"}
                            `}
                            onClick={() =>
                                setActiveStep(activeStep === step.stepNumber ? null : step.stepNumber)
                            }
                        >
                            {/* Main Step Content */}
                            <div className="p-6 flex items-center gap-4">
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center bg-blue-50 ${step.color}`}>
                                    <step.Icon size={28} />
                                </div>
                                <div>
                                    <h3 className={`text-lg font-semibold ${step.color}`}>
                                        {step.title}
                                    </h3>
                                </div>
                            </div>

                            {/* Expandable Content */}
                            <AnimatePresence>
                                {activeStep === step.stepNumber && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="p-6 bg-blue-50"
                                    >
                                        <p className="text-gray-600">
                                            {step.content}
                                        </p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ParallaxBusinessSetup;
